/**
 * API-ийн хариуны хэлбэр.
 *
 * ⚠️ Мөнгө БҮГД string (CLAUDE.md №3). `number` руу хөрвүүлж
 *    тооцоо хийхгүй — форматлахдаа `@boots/domain`-ыг ашиглана.
 */

import {
  CLAIM_RESOLUTION_MN, ITEM_STATUS_MN, PAYMENT_MN, PURCHASE_STATUS_MN, RETURN_DISPOSITION_MN, ROLE_MN,
  type RevenueRange, type SizeStockRow, type TransferStatusKey,
} from '@boots/domain';
import type {
  AuthUserOutput, DashboardPeriod, Gender, ItemStatus, LoginOutput,
  PhotoPresignOutput, PhotoRole, TotpChallengeOutput, TotpEnrollOutput, UserRole,
} from '@boots/schema';

export type { ItemStatus, UserRole };
export type { DashboardPeriod, Gender };

/**
 * ⚠️ Эдгээрийг ГАРААР дахин бичихгүй. `packages/schema` нь core, веб, гар
 *    утас гурвын ХУВААЛЦСАН контракт — хуулбарлавал сервер талын өөрчлөлт
 *    вэбэд чимээгүй мултарна.
 */
export type AuthUser = AuthUserOutput;
export type LoginResponse = LoginOutput;
export type TotpChallenge = TotpChallengeOutput;
export type TotpEnroll = TotpEnrollOutput;
export type PhotoRoleValue = PhotoRole;

export type LoginResult = LoginResponse | TotpChallenge;

export const isTotpChallenge = (r: LoginResult): r is TotpChallenge =>
  'totpRequired' in r;

/**
 * Өртөг харах эрхтэй дүрүүд. SALESPERSON өртөг ХАРАХГҮЙ.
 * ⚠️ `@boots/domain`-аас — сервер яг энэ жагсаалтаар `null` буцаадаг.
 */
export { COST_ROLES } from '@boots/domain';

export const PHOTO_ROLE_LABEL: Record<PhotoRoleValue, string> = {
  PRIMARY: 'Үндсэн',
  DETAIL: 'Дэлгэрэнгүй',
  SOLE: 'Ул',
  DEFECT: 'Гэмтэл',
  INVOICE: 'Нэхэмжлэх',
};

export interface Photo {
  id: string;
  role: PhotoRoleValue;
  sortOrder: number;
  capturedAt: string | null;
  createdAt: string;
  /** media боловсруулах хүртэл PENDING. */
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED' | 'PURGED';
  width: number | null;
  height: number | null;
  bytes: number;
  downgradedAt: string | null;
  /** Баталгаа дуусаад 6 сар — бүх хувилбар устсан. Бүх URL null. */
  purgedAt: string | null;
  /** Богино хугацаатай presigned URL. Боловсруулагдтал thumb/medium нь null. */
  originalUrl: string | null;
  mediumUrl: string | null;
  thumbUrl: string | null;
}

export type PhotoPresign = PhotoPresignOutput;

/**
 * Захирлын ханшийн мэдээлэл — GET /ref/fx/live.
 *
 * ⚠️ DB-д хадгалагддаггүй, ямар ч тооцоололд ашиглагддаггүй.
 *    `fx_rates` хүснэгттэй ХОЛБООГҮЙ.
 */
export interface FxLive {
  currency: 'RUB';
  /** 1 RUB хэдэн төгрөг. Эх сурвалж боломжгүй бол null. */
  rate: string | null;
  via: { base: 'USD'; usdToMnt: string; usdToRub: string } | null;
  sourceUpdatedAt: string | null;
  fetchedAt: string | null;
  stale: boolean;
  error: string | null;
}

export interface Store {
  id: string;
  code: string;
  name: string;
  type: 'WAREHOUSE' | 'RETAIL';
  address: string | null;
  phone: string | null;
  /** Газрын зураг дээрх байрлал. Хоёулаа эсвэл хоёулаа null. */
  lat: number | null;
  lng: number | null;
  /** Хариуцагч ажилтан — `users.storeId` (ажлын байр)-ААС ӨӨР */
  managerId: string | null;
  manager: { id: string; fullName: string } | null;
}

export interface Location {
  id: string;
  code: string;
  name: string;
  type: string;
  storeId: string | null;
  isSellable: boolean;
  isCountable: boolean;
  /** `?active=` шүүлтгүй жагсаалтад л ирнэ */
  isActive?: boolean;
  store: { code: string; name: string } | null;
}

export interface ProductModelRef {
  id: string;
  code: string;
  brand: string;
  name: string;
  material: string;
  /** Загварын анхдагч баталгаа — кассын сонголтын анхдагчийг үүнээс гаргана. */
  warrantyDays: number;
}

export interface ItemListRow {
  id: string;
  /**
   * Хөрөг зураг — сервер жагсаалт бүрд нэгийг өгнө (`coverPhotoByItem`).
   * `null` = зураггүй эсвэл баталгааны дараа устгагдсан.
   */
  photo?: ItemPhotoView | null;
  serial: string | null;
  /** ⚠️ Орост олноор бүртгэсэн гутал размергүй ирнэ — агуулах бөглөнө. */
  size: number | null;
  status: ItemStatus;
  priceMnt: string;
  /** `null` = өртөг тооцоогүй (2026-09-16-наас хойш бүртгэгдсэн гутал). */
  landedCostMnt: string | null;
  /** Нийлүүлэгчээс зээлээр авсан — мөнгө хараахан төлөгдөөгүй. */
  isCredit: boolean;
  collectedAt: string;
  labeledAt: string | null;
  syncedAt: string | null;
  version: number;
  model: ProductModelRef;
  /** `type` нь скан дээр ШАЛТГААН хэлэхэд хэрэгтэй (`sellableReason`). */
  location: {
    id: string;
    code: string;
    name: string;
    type: string;
    isSellable: boolean;
    storeId: string | null;
    /** «Салбар › Байршил» — `formatLocation` (№13) */
    store: { name: string } | null;
  } | null;
  supplier: { id: string; code: string; name: string };
}

export interface ItemDetail extends ItemListRow {
  publicToken: string | null;
  purchaseAmount: string;
  currency: string;
  /** `null` — систем ₽-ийг ₮ болгож бодохгүй (шийдвэр 2026-09-16). */
  purchaseRate: string | null;
  purchaseRateDate: string | null;
  goodsCostMnt: string | null;
  overheadMnt: string;
  warrantyDaysOverride: number | null;
  supplier: { id: string; code: string; name: string; region: string | null };
  purchase: { id: string; code: string | null; purchasedAt: string; status: string; notes: string | null };
  photos: { id: string; role: string; sortOrder: number; originalKey: string }[];
}

/**
 * `GET /items/:id/photos` — харах URL-тай хувилбар.
 *
 * ⚠️ Энэ нь `ItemDetail.photos`-оос ТУСДАА дуудагдана: presigned URL нь
 *    10 минутын настай (`services/core/src/lib/s3.ts`) тул удаан кэшлэгдэх
 *    дэлгэрэнгүй хариунд орох ёсгүй.
 *
 * `thumbUrl`/`mediumUrl` нь media боловсруулж дуустал `null` — тэр үед
 * `originalUrl` руу шилжинэ (`services/core/src/modules/photos.ts`).
 */
export interface ItemPhotoView {
  id: string;
  role: PhotoRoleValue;
  sortOrder: number;
  capturedAt: string | null;
  createdAt: string;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED' | 'PURGED';
  width: number | null;
  height: number | null;
  bytes: number;
  downgradedAt: string | null;
  /** Баталгаа дуусаад 6 сар — бүх хувилбар устсан. Бүх URL null. */
  purgedAt: string | null;
  originalUrl: string | null;
  mediumUrl: string | null;
  thumbUrl: string | null;
}

export interface Movement {
  id: string;
  reason: string;
  note: string | null;
  movedAt: string;
  refType: string | null;
  refId: string | null;
  fromLocation: { id: string; code: string; name: string } | null;
  toLocation: { id: string; code: string; name: string };
  movedBy: { id: string; username: string };
}

export const GENDER_LABEL: Record<Gender, string> = {
  MEN: 'Эр',
  WOMEN: 'Эм',
  UNISEX: 'Нийтийн',
  KIDS: 'Хүүхэд',
};

export interface StockReport {
  warehouseCount: number;
  newThisWeek: number;
  movedToRetail: number;
  /** SALESPERSON-д null */
  value: { landedCostMnt: string; avgLandedMnt: string } | null;
  /** `size: null` = размер бөглөгдөөгүй гутал */
  sizes: { size: number | null; count: number }[];
}

/**
 * Салбарын тайлан — `GET /reports/branches`.
 *
 * ⚠️ `type` нь агуулах/жижиглэнг ялгах ЦОРЫН ГАНЦ зам. Дашбоардын
 *    `/reports/dashboard` хариу нь байршил тус бүрийн `storeName`-ыг
 *    л өгдөг, дэлгүүрийн ТӨРЛИЙГ өгдөггүй — тиймээс «салбарт байгаа»
 *    гэсэн тоог тэндээс гаргаж БОЛОХГҮЙ, нэрээр таамаглах нь эмзэг.
 */
export interface Branch {
  id: string;
  code: string;
  name: string;
  type: 'WAREHOUSE' | 'RETAIL';
  address: string | null;
  locations: number;
  /** Бүх төлөвийн гутал — бэлэн, захиалсан, шошгогүй ч мөн */
  items: number;
  /** `AVAILABLE` + байршил нь зарагдах */
  sellable: number;
  unlabeled: number;
  staff: number;
  /** SALESPERSON-д null */
  landedCostMnt: string | null;
}

export interface BranchTransferRow {
  day: string;
  from: string;
  to: string;
  by: string;
  count: number;
}

export interface BranchReport {
  branches: Branch[];
  transfers: BranchTransferRow[];
}

/** `/transfers` ба `/transfers/inbound` — шилжүүлгийн мөр. */
export interface TransferRow {
  id: string;
  code: string;
  status: TransferStatusKey;
  sentCount: number;
  receivedCount: number;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  notes: string | null;
  fromStore: { id: string; code: string; name: string };
  toStore: { id: string; code: string; name: string };
  sentBy: { username: string; fullName: string } | null;
  receivedBy: { username: string; fullName: string } | null;
  /** Гутлын нийт ЗАРАХ үнэ (одоогийн үнээр) — зөвхөн жагсаалтад (2026-10-30) */
  valueMnt?: string;
}

export interface TransferItemRow {
  id: string;
  scannedOut: boolean;
  /** Хүлээн авсны дараа = «ирсэн гэж баталгаажсан» (уншуулаагүй ч) */
  scannedIn: boolean;
  scannedInAt: string | null;
  item: {
    id: string;
    serial: string | null;
    size: number | null;
    status: string;
    priceMnt: string;
    model: { code: string; name: string };
    location: { name: string } | null;
  };
}

export interface TransferDetail extends TransferRow {
  items: TransferItemRow[];
}

/** Борлуулалтын нэг нүд — мөнгө string (CLAUDE.md №3). */
export interface SalesTally {
  units: number;
  revenueMnt: string;
}

/**
 * Салбарын самбар — `GET /reports/branch-dashboard`.
 *
 * ⚠️ Өртөг ОГТ БАЙХГҮЙ — салбарын ажилтанд зориулсан. Орлого = зарсан үнэ.
 *    `store: null` = энэ хэрэглэгчид харах салбар оноогоогүй.
 */
export type BranchDashboard = BranchDashboardData | { store: null; stores: BranchPick[] };

export interface BranchDashboardData {
  store: BranchPick;
  /** Сонгож болох салбарууд — нэгээс олон бол солих таб гарна */
  stores: BranchPick[];
  stock: {
    /** Салбарт зарагдахуйц төлөвтэй — лангуу + агуулах өрөө */
    onHand: number;
    /** Үүнээс лангуун дээр (зарагдах байршилд) */
    onFloor: number;
    /** Лангуун дээр 0₮ — касс уншуулахгүй */
    zeroPriced: number;
  };
  /** Хяналтад байгаа размерууд — дүрэм нь `@boots/domain` → `sizeStockRows` */
  sizes: SizeStockRow[];
  /** Агуулахаас энэ сард ИРСЭН (шилжүүлэг хаагдсан) гутал */
  pulled: { month: number };
  inTransit: { id: string; code: string; from: string; sentAt: string | null; count: number }[];
  sales: {
    today: SalesTally;
    month: SalesTally;
    /** Өмнөх сарын МӨН ҮЕ — бүтэн сар биш */
    prev: SalesTally;
    /** Энэ сарын өдөр бүр, өнөөдрийг оруулаад. `date` = УБ-ийн "2026-09-01" */
    daily: (SalesTally & { date: string })[];
  };
  /** Сарын зорилт тавиагүй бол null */
  target: { targetMnt: string; pct: number } | null;
}

export interface BranchPick {
  id: string;
  code: string;
  name: string;
}

/**
 * Агуулахын дэлгэрэнгүй — `GET /reports/warehouses`.
 *
 * `Branch`-аас ЯЛГААТАЙ: тэр нь бүх нэгжийн нэг мөр товчоо, энэ нь
 * агуулах бүрийн дотоод байршил, төлөв, ажилтан. Гутлын жагсаалт
 * энд БАЙХГҮЙ — `GET /items?storeId=` тусдаа хуудаслана.
 */
export interface WarehouseLocationRow {
  id: string;
  code: string;
  name: string;
  type: string;
  isSellable: boolean;
  isCountable: boolean;
  items: number;
}

export interface WarehouseStaffRow {
  id: string;
  username: string;
  fullName: string;
  phone: string | null;
  role: UserRole;
  isActive: boolean;
  lastLoginAt: string | null;
}

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  address: string | null;
  phone: string | null;
  items: number;
  sellable: number;
  unlabeled: number;
  /** SALESPERSON-д null */
  landedCostMnt: string | null;
  byStatus: Partial<Record<ItemStatus, number>>;
  locations: WarehouseLocationRow[];
  staff: WarehouseStaffRow[];
}

export interface WarehouseReport {
  warehouses: Warehouse[];
}

/** Жижиглэнгийн салбаруудад байгаа нийт гутал. */
export function retailItemCount(report: BranchReport | undefined): number | undefined {
  if (!report) return undefined;
  return report.branches.reduce((a, b) => (b.type === 'RETAIL' ? a + b.items : a), 0);
}

export interface Dashboard {
  counts: {
    total: number;
    sellable: number;
    unlabeled: number;
    unsynced: number;
    /** Зарагдах байршилд 0₮ үнэтэй зогсож буй гутал */
    zeroPriced: number;
    byStatus: Partial<Record<ItemStatus, number>>;
  };
  /** SALESPERSON-д null — өртөг харуулахгүй */
  value: {
    onHandCount: number;
    landedCostMnt: string;
    retailMnt: string;
    avgPriceMnt: string;
  } | null;
  /**
   * Нийлүүлэгчид төлөх өр — ЗӨВХӨН ₽. Ханшаар ₮ болгож хөрвүүлэхгүй
   * (шийдвэр 2026-09-16). SALESPERSON-д бүхэлдээ null.
   */
  credit: {
    currency: string;
    balanceRub: string;
    /** Төлөгдөөгүй аялалын тоо */
    purchases: number;
    /** Өртэй нийлүүлэгчийн тоо */
    suppliers: number;
    /** Хамгийн эртний төлөгдөөгүй аялал */
    oldestAt: string | null;
    /** Нээлттэй зээлийн төлөгдсөн хувь (0–100) */
    paidPct: number;
    /**
     * Тохирсон хугацаа нь хэтэрсэн аялалын тоо ба дүн.
     *
     * ⚠️ Push мэдэгдэл БАЙХГҮЙ (Firebase хориотой) — админ, захирал
     *    вэб нээхэд энэ тоо дашбоардын анхааруулга болно.
     */
    overdue: number;
    overdueRub: string;
    /** Хамгийн ойрын төлөх хугацаа. */
    nextDueAt: string | null;
  } | null;
  fx: {
    currency: string;
    rate: string | null;
    prev: string | null;
    avg7: string | null;
    history: { date: string; rate: string }[];
  };
  intake: {
    period: DashboardPeriod;
    /**
     * `value` = нийт · `trip` = аяллаар орсон · `opening` = эхний
     * үлдэгдэл (шошгоны огноогоор). Статистикт ХОЛИХГҮЙ — №2.
     */
    series: { label: string; value: number; trip: number; opening: number }[];
  };
  /** Гутал хаанаас бүртгэгдсэн — нийт ба одоо байгаа (№2). */
  sources: Record<'trip' | 'opening', { total: number; onHand: number }>;
  /**
   * Байршил бүрийн үлдэгдэл. ⚠️ Гутал ХААНА байгааг ЗӨВХӨН эндээс
   * уншина — `ItemStatus` нь байршил илэрхийлдэггүй (CLAUDE.md №9).
   */
  locations: {
    id: string; code: string; name: string;
    /** `LocationType` — SUPPLIER · TRANSIT_INTL · SALES_FLOOR … */
    type: string;
    isSellable: boolean;
    storeName: string | null;
    /** null = дэлгүүрт харьяалагдахгүй (нийлүүлэгч, зам, худалдагдсан) */
    storeType: 'WAREHOUSE' | 'RETAIL' | null;
    count: number;
  }[];
  nav: {
    purchases: number;
    items: number;
    orders: number;
    stores: number;
    staff: number;
    suppliers: number;
  };
}

/**
 * «Нөөц ба байршил» — `GET /reports/stock-locations` (№4, №5).
 *
 * `items` = байршилд БИЕТЭЭР байгаа гутал (төлөв харгалзахгүй) —
 * «Бүх гутал» `?locationId=&status=all`-тай таарна.
 */
export type StockScope = 'all' | 'retail' | 'warehouse';

export interface StockLocations {
  scope: StockScope;
  totals: { items: number; sellable: number; unlabeled: number; landedCostMnt: string | null };
  /** Нэгжид харьяалагдахгүй — зам, Орос, идэвхгүй байршил */
  outside: number;
  units: {
    id: string; code: string; name: string; type: 'WAREHOUSE' | 'RETAIL';
    items: number; sellable: number; unlabeled: number;
    /** Өртөг харах эрхгүйд null */
    landedCostMnt: string | null;
    locations: { id: string; code: string; name: string; type: string; isSellable: boolean; items: number }[];
  }[];
  sizes: { size: number | null; count: number }[];
}

/** `LocationType` — байршлын төрлийн нэр (№13). */
export const LOCATION_TYPE_LABEL: Record<string, string> = {
  SUPPLIER: 'Нийлүүлэгч дээр',
  TRANSIT_INTL: 'Орос → Монгол замд',
  WAREHOUSE_INTAKE: 'Хүлээн авалт',
  WAREHOUSE_MAIN: 'Үндсэн агуулах',
  TRANSIT_INTERNAL: 'Салбар хооронд замд',
  SALES_FLOOR: 'Худалдааны танхим',
  BACK_ROOM: 'Агуулах өрөө',
  DISPLAY: 'Үзмэр',
  REPAIR: 'Засвар',
  QUARANTINE: 'Тусгаарлалт',
  CUSTOMER: 'Худалдан авагчид',
  DISPOSED: 'Актлагдсан',
};

/** Худалдан авалт — `GET /purchases`. Мөнгө бүгд string. */
export interface PurchaseRow {
  id: string;
  code: string | null;
  purchasedAt: string;
  currency: string;
  goodsAmount: string;
  transportMnt: string;
  customsMnt: string;
  otherMnt: string;
  allocation: 'BY_VALUE' | 'BY_UNIT';
  itemCount: number;
  status: 'DRAFT' | 'SYNCED' | 'COSTED' | 'RECEIVED';
  syncedAt: string | null;
  costedAt: string | null;
  receivedAt: string | null;
  /** Цуглуулагч аялалаа дуусгасан мөч. ⚠️ `costedAt`-аас ӨӨР ойлголт. */
  closedAt: string | null;
  supplier: { id: string; code: string; name: string; region: string | null };
  collector: { id: string; username: string; fullName: string };
  overheadTotalMnt: string;
  goodsCostMnt: string;
  landedCostMnt: string;
  itemsRegistered: number;
}

export interface PurchaseListMeta {
  count: number;
  total: number;
  totals: {
    items: number;
    goodsCostMnt: string;
    overheadMnt: string;
    landedCostMnt: string;
  };
}

/**
 * Аялалын нэг мөр задаргаа — загвар × размер.
 *
 * ⚠️ `PurchaseDetail.items` нь 100-аар тасардаг тул «ямар гутал хэдээр»
 *    гэдгийг ТҮҮНЭЭС тоолж болохгүй. Энэ нь сервер талын groupBy —
 *    аялалын БҮХ нэгжийг хамарна.
 */
export interface PurchaseBreakdownRow {
  modelId: string;
  model: { id: string; code: string; brand: string; name: string; gender: Gender } | null;
  size: number;
  count: number;
  goodsCostMnt: string;
  landedCostMnt: string;
}

/**
 * Аялалын ТУЛГАЛТ — «хэд ирэх ёстой байсан, одоо хаана хэд байна».
 *
 * ⚠️ Байршлаар групплэгддэг, ТӨЛӨВӨӨР биш: `ItemStatus` нь гутал хаана
 *    байгааг заадаггүй (CLAUDE.md №9).
 */
export interface PurchaseReconciliation {
  /** `purchases.item_count` — цуглуулагчийн заасан тоо. 0 = заагаагүй */
  expected: number;
  /** DB-д бодитоор үүссэн мөр */
  registered: number;
  labeled: number;
  unlabeled: number;
  /** Байршил тодорхойгүй — агуулахад хүлээж аваагүй */
  unlocated: number;
  byStore: { storeId: string; storeName: string; storeType: string; count: number }[];
  byLocation: {
    locationId: string;
    locationCode: string | null;
    locationName: string | null;
    isSellable: boolean;
    storeId: string | null;
    storeName: string | null;
    storeType: string | null;
    count: number;
  }[];
}

/** Аялалын дэлгэрэнгүй — `GET /purchases/:id`. */
export interface PurchaseDetail extends PurchaseRow {
  costedBy: { id: string; username: string; fullName: string } | null;
  reconciliation: PurchaseReconciliation;
  totals: {
    items: number;
    goodsCostMnt: string;
    overheadMnt: string;
    landedCostMnt: string;
    byStatus: Record<string, number>;
  };
  /**
   * Зээлээр авсан — нийлүүлэгчид өгөх өр.
   *
   * ⚠️ `amount` нь АЯЛЛЫН валютаар (`currency`, ихэвчлэн ₽), төгрөгөөр
   *    биш: өр тэр валютаар үүссэн, тээвэр гааль нь түүнд хамаагүй.
   */
  credit: { count: number; amount: string };
  close: PurchaseClose;
  breakdown: PurchaseBreakdownRow[];
  itemsTruncated: boolean;
}

/**
 * Аялал дуусгах — цуглуулагчийн ЗАРЛАСАН нийлбэр ба гутлын мөрүүдтэй
 * харьцуулсан зөрүү.
 *
 * ⚠️ ₽ ба ₮ нь ТУСДАА бодит төлбөр, нэг нь нөгөөгийн хөрвүүлэг БИШ.
 *    Гутлын мөрүүд рублиэр тул зөрүү зөвхөн ₽ талд гарна; зарласан ₮
 *    нь эсрэг талгүй төлбөрийн бүртгэл.
 *
 * ⚠️ `variance` эерэг = зарласан нь мөрүүдээс их, өөрөөр хэлбэл гутал
 *    дутуу бүртгэгдсэн байх магадлалтай.
 */
export interface PurchaseClose {
  closedAt: string | null;
  closedBy: { id: string; username: string; fullName: string } | null;
  /** `count` — цуглуулагчийн зарласан ширхэг (бөөн бүртгэл). */
  declared: { totalRub: string; creditRub: string; totalMnt: string; creditMnt: string; count: number };
  /** Зээлээ хэзээ төлөхөөр тохирсон. Зээлгүй аялалд null. */
  creditDueDate: string | null;
  items: { totalRub: string; creditRub: string; count: number };
  variance: { totalRub: string; creditRub: string };
}

/**
 * `GET /purchases/:id/items` — бүртгэсэн гутал, гутал бүрд нэг хөрөг.
 *
 * ⚠️ `id`-ээр (UUID v7) эрэмбэлэгдэж ирнэ = бүртгэсэн дараалал. Веб нь
 *    мөрүүдийг «нэг суулт» болгон бүлэглэдэг тул энэ дараалал чухал.
 */
export interface PurchaseItemRow {
  id: string;
  serial: string | null;
  tempCode: string | null;
  size: number | null;
  status: ItemStatus;
  purchaseAmount: string;
  currency: string;
  isCredit: boolean;
  collectedAt: string;
  priceMnt: string;
  goodsCostMnt: string | null;
  landedCostMnt: string | null;
  model: { id: string; code: string; brand: string; name: string; gender: Gender };
  location: { id: string; code: string; name: string; store: { name: string } | null } | null;
  photo: ItemPhotoView | null;
}

/** ⚠️ `@boots/domain`-аас — гар утас ижил тольтой байх ёстой. */
export const PURCHASE_STATUS_LABEL = PURCHASE_STATUS_MN;

/**
 * `GET /purchases/:id/defects` — хайрцаг задлахад илэрсэн гэмтэл.
 *
 * Цуцлагдсан мөр ч ирнэ (`voidedAt`): оноо аль хэдийн харагдсан байж
 * болох тул бүртгэл алга болдоггүй, зураастай харагдана.
 */
export interface ReceivingDefect {
  id: string;
  severity: 'MINOR' | 'MAJOR';
  note: string | null;
  reportedAt: string;
  voidedAt: string | null;
  type: { id: string; code: string; labelMn: string };
  item: { id: string; serial: string | null; size: number | null } | null;
  reportedBy: { id: string; fullName: string };
}

export const DEFECT_SEVERITY_LABEL: Record<ReceivingDefect['severity'], string> = {
  MINOR: 'Бага',
  MAJOR: 'Ноцтой',
};

export const ALLOCATION_LABEL: Record<PurchaseRow['allocation'], string> = {
  BY_VALUE: 'Үнийн хувиар',
  BY_UNIT: 'Ширхэгт тэнцүү',
};

/**
 * `GET /purchases/:id/cost-preview` — бичихгүй тооцоо.
 *
 * ⚠️ DB-д ХАДГАЛСАН зардлаас бодогдоно, маягтын утгаас биш.
 */
export interface CostPreview {
  method: PurchaseRow['allocation'];
  alreadyCosted: boolean;
  overheadTotalMnt: string;
  roundingAdjustmentMnt: string;
  lines: {
    itemId: string;
    serial: string | null;
    size: number | null;
    goodsCostMnt: string | null;
    overheadMnt: string;
    landedCostMnt: string | null;
  }[];
}

/** Захиалга — `GET /orders`. */
export interface OrderRow {
  id: string;
  code: string;
  channel: 'STORE' | 'ONLINE' | 'WHOLESALE';
  status: 'ACTIVE' | 'VOIDED';
  subtotalMnt: string;
  discountMnt: string;
  /** Төлөх дүн — нэмэгдэл ОРСОН. Орлого нь мөрүүдийн нийлбэр (нэмэгдэлгүй). */
  totalMnt: string;
  /** Хуучин захиалгын хөлдсөн зээлийн 7% (2026-09-28-нд цуцлагдсан, шинэд 0) — орлого биш */
  creditFeeMnt: string;
  /** И-баримт +10% НӨАТ — орлого биш */
  vatMnt: string;
  payment: string;
  /**
   * Төлбөрийн задаргаа — «500,000₮ бэлнээр, үлдсэнийг LendMN-ээр».
   * Нэг хэлбэрээр төлсөн ч НЭГ мөр ирнэ (сервер үргэлж бичдэг).
   */
  payments: {
    id: string;
    method: string;
    amountMnt: string;
    provider: { id: string; code: string; labelMn: string } | null;
  }[];
  soldAt: string;
  voidedAt: string | null;
  voidReason: string | null;
  store: { id: string; code: string; name: string };
  soldBy: { id: string; username: string; fullName: string };
  customer: { id: string; phone: string; name: string | null } | null;
  /** Өгсөн урамшуулал (2026-11-03) — хуучин баримтад `null` */
  promotion?: { id: string; labelMn: string } | null;
  _count: { lines: number };
}

export interface OrderListMeta {
  count: number;
  total: number;
  totals: { activeOrders: number; totalMnt: string };
}

/** `GET /reports/sales`. Өртөг, ашиг нь эрхээс хамаарч null. */
export interface SalesReport {
  from: string;

  groupBy: string;
  totals: {
    units: number;
    revenueMnt: string;
    costMnt: string | null;
    /** Өртөг тооцоогүй тул ашигт ороогүй ширхэг — ашиг зөвхөн бусдаас бодогдоно. */
    uncostedUnits: number;
    profitMnt: string | null;
    marginPct: string | null;
    commissionMnt: string | null;
  };
  groups: {
    key: string;
    label: string;
    units: number;
    revenueMnt: string;
    uncostedUnits: number;
    profitMnt: string | null;
  }[];
}

/** CSV экспорттой нэг толь — `@boots/domain`-д. */
export const PAYMENT_LABEL = PAYMENT_MN;

/**
 * Буцаалтын шийдэл, баталгааны нэхэмжлэлийн шийдвэр.
 *
 * ⚠️ Гурван хуудас (захиалга, баталгаа, гутлын түүх) ИЖИЛ үгээр
 *    ярих ёстой — өмнө нь тус тусдаа хуулбартай байв.
 */
export const RETURN_DISPOSITION_LABEL = RETURN_DISPOSITION_MN;

export const CLAIM_RESOLUTION_LABEL = CLAIM_RESOLUTION_MN;

/**
 * Төлвийн монгол нэр — enum-ийг шууд дэлгэцэд гаргахгүй.
 * Эх нь `@boots/domain` (тайлангийн файл ижил үг хэрэглэнэ).
 */
export const STATUS_LABEL: Record<ItemStatus, string> = ITEM_STATUS_MN;

export const MOVEMENT_LABEL: Record<string, string> = {
  COLLECT: 'Цуглуулав',
  SHIP: 'Илгээв',
  RECEIVE: 'Хүлээн авав',
  LABEL: 'Шошго наав',
  TRANSFER: 'Шилжүүлэв',
  SELL: 'Зарав',
  RETURN: 'Буцаав',
  REPAIR: 'Засварт',
  WRITE_OFF: 'Актлав',
  ADJUST: 'Тохируулав',
};

export const MATERIAL_LABEL: Record<string, string> = {
  REINDEER: 'Цаа буганы арьс',
  LEATHER: 'Арьс',
  FELT: 'Эсгий',
  OTHER: 'Бусад',
};

export const ROLE_LABEL: Record<UserRole, string> = ROLE_MN;

/**
 * Орлогын график — `GET /reports/revenue`.
 *
 * ⚠️ Мөнгө БҮГД string (CLAUDE.md №3). Клиент дээр `Number()` руу
 *    хөрвүүлэх нь ЗӨВХӨН зурах координат бодоход зөвшөөрөгдөнө —
 *    харуулах дүнг `formatMnt`/`formatMntShort` string дээр ажиллана.
 */
export interface RevenuePoint {
  /** `2026-08-24` · `2026-08-24T14` · `2026-08` */
  bucket: string;
  revenueMnt: string;
  orderCount: number;
}

export interface RevenueReport {
  range: RevenueRange;
  timezone: string;
  /** Цонхны эхлэл. `to` нь ЗӨРИУД байхгүй — `revenue.ts` дахь etag-ийн тайлбар */
  from: string;
  points: RevenuePoint[];
  /** Өмнөх ижил урттай хугацаа, индекс тус бүрээр зэрэгцүүлсэн */
  previousPoints: string[];
  total: string;
  previousTotal: string;
  /** Өмнөх нь 0 бол null — «∞%» гэж худал харуулахгүй */
  changePercent: number | null;
  orderCount: number;
}

/**
 * Өрсөлдөөний эрэмбэ — `GET /leaderboard`.
 *
 * ⚠️ САЛБАРУУД БАГААРАА өрсөлдөнө. `subjectType: 'BRANCH'` үед
 *    субьект нь САЛБАР — ажилтны хувийн эрэмбэ ХАА Ч БАЙХГҮЙ.
 * ⚠️ Оноо string (CLAUDE.md №3 — Decimal).
 */
export interface ScoreRow {
  metric: string;
  label: string;
  raw: number;
  normalized?: number;
  weight: number;
  /** 1-ээс өөр бол тухайн субьект зарим үзүүлэлтээс чөлөөлөгдсөн */
  scale?: number;
  points: string;
}

export interface LeaderboardEntry {
  rank: number;
  previousRank: number | null;
  subject: { id: string; name: string };
  totalScore: string;
  breakdown: ScoreRow[];
  /** Хамаараагүй үзүүлэлт — «зорилтгүй», «шинэ» */
  excluded: string[];
  meta?: {
    revenueMnt?: string;
    staffCount?: number;
    isNew?: boolean;
    hasTarget?: boolean;
    defectRate?: number;
    defectCount?: number;
  };
}

export interface LeaderboardReport {
  competition: string;
  subjectType: 'BRANCH' | 'EMPLOYEE';
  period: { type: string; key: string; label: string };
  /** Дууссан хугацаа — эрэмбэ хөлдсөн */
  isFinalized: boolean;
  entries: LeaderboardEntry[];
  updatedAt: string;
}

export interface CompetitionInfo {
  key: string;
  label: string;
  subjectType: 'BRANCH' | 'EMPLOYEE';
  metrics: { metric: string; label: string; penalty: boolean }[];
}
